import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toolSlug } from "@/lib/toolSlug";
import { useToolStats } from "@/hooks/useToolStats";

type Tool = {
  id: string;
  company: string;
  title: string;
  value: string;
  category: string;
};

const ToolsTeaser = () => {
  const [tools, setTools] = useState<Tool[]>([]);
  const [loading, setLoading] = useState(true);
  const [showFallback, setShowFallback] = useState(false);
  const { totalValueLabel, loading: statsLoading } = useToolStats();

  useEffect(() => {
    let cancelled = false;
    const timeout = setTimeout(() => {
      if (!cancelled) setShowFallback(true);
    }, 3000);

    (async () => {
      const { data, error } = await supabase
        .from("tools")
        .select("id, company_name, program_name, credit_value, category, is_featured, display_order")
        .eq("is_active", true)
        .order("is_featured", { ascending: false })
        .order("display_order", { ascending: true })
        .limit(6);

      if (cancelled) return;
      if (error) {
        console.error("Failed to load tools:", error);
        setTools([]);
      } else {
        setTools(
          (data ?? []).map((t) => ({
            id: String(t.id),
            company: t.company_name,
            title: t.program_name,
            value: t.credit_value ?? "",
            category: t.category ?? "Other Services",
          }))
        );
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, []);

  return (
    <section className="container mx-auto px-4 sm:px-6 lg:px-8 mb-20">
      <div className="mb-8 text-center">
        <Link to="/tools" className="inline-block group">
          <h2 className="text-3xl sm:text-4xl font-black text-foreground tracking-tight group-hover:underline underline-offset-4 decoration-2">
            Discover {statsLoading ? "—" : totalValueLabel} in free tools
          </h2>
          <p className="text-muted-foreground mt-2 text-lg">
            Every tool used in the book — with free credits, startup perks, and chapter references.
          </p>
        </Link>
      </div>

      {loading ? (
        showFallback ? (
          <div className="text-center py-12 mb-8">
            <Link
              to="/tools"
              className="inline-block font-bold text-lg text-primary hover:underline underline-offset-4"
            >
              Explore our full AI toolkit →
            </Link>
          </div>
        ) : (
          <div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8"
            aria-busy="true"
            aria-label="Loading tools"
          >
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="brutal-border bg-card rounded-md flex flex-col h-full overflow-hidden animate-pulse"
              >
                <div className="brutal-border border-t-0 border-l-0 border-r-0 px-5 py-3">
                  <div className="h-4 w-24 bg-muted rounded" />
                </div>
                <div className="p-5 flex flex-col gap-3">
                  <div className="h-5 w-3/4 bg-muted rounded" />
                  <div className="h-8 w-1/2 bg-muted rounded" />
                  <div className="h-5 w-20 bg-muted rounded-full" />
                </div>
              </div>
            ))}
          </div>
        )
      ) : tools.length === 0 ? (
        <div className="text-center py-12 mb-8">
          <Link
            to="/tools"
            className="inline-block font-bold text-lg text-primary hover:underline underline-offset-4"
          >
            Explore our full AI toolkit →
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {tools.map((tool) => (
            <Link
              key={tool.id}
              to={`/tools/${toolSlug(tool.company, tool.title)}`}
              className="brutal-border brutal-shadow bg-card rounded-md flex flex-col h-full hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all"
            >
              <div className="brutal-border border-t-0 border-l-0 border-r-0 px-5 py-3">
                <span className="font-black text-sm tracking-wide text-foreground">{tool.company}</span>
              </div>
              <div className="p-5 flex flex-col flex-1 gap-3">
                <h3 className="font-bold text-lg text-foreground leading-tight">{tool.title}</h3>
                {tool.value && <div className="text-3xl font-black text-foreground">{tool.value}</div>}
                <span className="inline-block brutal-border text-xs font-bold px-3 py-1 rounded-full uppercase tracking-[0.08em] bg-primary/20 text-foreground self-start">
                  {tool.category}
                </span>
              </div>
            </Link>
          ))}
        </div>
      )}

      <div className="text-center">
        <Link
          to="/tools"
          className="inline-flex brutal-border brutal-shadow-lg bg-primary text-primary-foreground font-bold px-8 py-3 text-lg hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all rounded-md uppercase tracking-[0.08em]"
        >
          Explore All Tools →
        </Link>
      </div>
    </section>
  );
};

export default ToolsTeaser;
